import { getAttributeValue, isConditionActive, } from '@canvas-logic/clp-domain';
import { assertUnreachable } from '@canvas-logic/clp-utils';
import { getSchemaFor } from '../materials/MaterialSchema';
import { TEXTURE_SCHEMA } from '../textures/TextureSchema';
export class BuilderPlatformInfo {
    constructor(view3DConfiguration, dynamicValueService, partsRegistry, meta = {}) {
        this.view3DConfiguration = view3DConfiguration;
        this.dynamicValueService = dynamicValueService;
        this.partsRegistry = partsRegistry;
        this.meta = meta;
        this.product = {};
        this.partsRegistry.addParts(this.view3DConfiguration.parts);
    }
    setProduct(product) {
        this.product = product;
    }
    getProduct() {
        return this.product;
    }
    getMeta() {
        return this.meta;
    }
    setMeta(meta) {
        this.meta = meta;
    }
    getModel(modelKey) {
        return this.findModel((model) => model.key === modelKey);
    }
    findModel(predicate) {
        return this.view3DConfiguration.views.find(predicate);
    }
    getParts() {
        return this.partsRegistry.getParts(this.product);
    }
    getHiddenMeshesForPart(part) {
        const meshesVisibility = {};
        for (const rule of part.rules) {
            if (rule.type === 'hideMeshRule') {
                const isActive = isConditionActive(rule.condition, this.product);
                rule.meshes.forEach((mesh) => {
                    var _a, _b;
                    if (!(mesh in meshesVisibility)) {
                        meshesVisibility[mesh] = {
                            scene: true,
                            webAr: true,
                        };
                    }
                    if (rule.hideInScene) {
                        (_a = meshesVisibility[mesh]).scene && (_a.scene = !isActive);
                    }
                    if (rule.hideInWebAR) {
                        (_b = meshesVisibility[mesh]).webAr && (_b.webAr = !isActive);
                    }
                });
            }
        }
        const specs = [];
        for (const [mesh, options] of Object.entries(meshesVisibility)) {
            specs.push({
                mesh,
                visible: options.scene,
                webAr: { visible: options.webAr },
            });
        }
        return specs;
    }
    getTextures() {
        const textures = this.view3DConfiguration.textures || [];
        return textures.map((texture) => {
            var _a;
            const params = { url: texture.file };
            for (const [optionName, optionValue] of Object.entries((_a = texture.options) !== null && _a !== void 0 ? _a : {})) {
                if (!(optionName in TEXTURE_SCHEMA)) {
                    console.warn(`Unknown option name ${optionName} in texture ${texture.id}`);
                }
                params[optionName] = this.dynamicValueService.calculate(optionValue, this.product);
            }
            return {
                key: texture.id,
                params,
            };
        });
    }
    getPartMeshes(partKey) {
        const part = this.view3DConfiguration.parts.find((part) => part.partKey === partKey);
        if (!part) {
            throw new Error(`Cannot find part: ${partKey}`);
        }
        return this.createPartMaterialSpecification(part.materials, this.product);
    }
    createPartMaterialSpecification(materialConfigurations, configuration) {
        const result = [];
        for (const materialConfiguration of materialConfigurations) {
            const materialReferenceId = this.dynamicValueService.calculate(materialConfiguration.material, configuration);
            if (!materialReferenceId) {
                console.warn(`Skipped material for "${JSON.stringify(materialConfiguration.material)}"`);
                continue;
            }
            const material = {
                materialKey: materialReferenceId,
                meshName: materialConfiguration.mesh,
            };
            result.push(material);
        }
        return result;
    }
    getMaterials() {
        var _a, _b;
        const configuration = this.product;
        const materials = [];
        for (const material of (_a = this.view3DConfiguration.materials) !== null && _a !== void 0 ? _a : []) {
            const type = material.type;
            switch (type) {
                case 'physicalMaterial':
                case 'standardMaterial': {
                    const params = {
                        type,
                    };
                    const schema = getSchemaFor(material.type);
                    for (const [optionName, optionValue] of Object.entries(material.options)) {
                        if (!(optionName in schema)) {
                            console.warn(`Unknown optionName ${optionName} in material ${type}`);
                        }
                        params[optionName] = this.dynamicValueService.calculate(optionValue, configuration);
                    }
                    materials.push({ key: material.id, params: params });
                    break;
                }
                case 'shaderMaterial': {
                    const params = {
                        type: 'shaderMaterial',
                        fragmentShader: material.shader.fragment,
                        vertexShader: material.shader.vertex,
                        transparent: (_b = material.shader.transparent) !== null && _b !== void 0 ? _b : false,
                        uniforms: material.shader.uniforms,
                    };
                    materials.push({
                        key: material.id,
                        params,
                    });
                    break;
                }
                case 'meshMaterial':
                    throw new Error('Not supported');
                default:
                    assertUnreachable(type, 'Invalid material type');
            }
        }
        return materials;
    }
    getComponentModel(componentKey) {
        const variant = this.product[componentKey];
        if (!variant) {
            return;
        }
        const modelIdAttribute = variant['_model3dId'];
        if (!modelIdAttribute) {
            return;
        }
        const modelId = getAttributeValue(modelIdAttribute);
        const model = this.findModel((model) => model.id === modelId);
        if (!model) {
            return;
        }
        return model;
    }
    getModelJP(model, jpKey) {
        return model.model3dJointPoints.find((jp) => jp.key === jpKey);
    }
    getComponentJointPoint(componentKey, jpKey) {
        const model = this.getComponentModel(componentKey);
        if (!model) {
            return;
        }
        return this.getModelJP(model, jpKey);
    }
}
//# sourceMappingURL=BuilderPlatformInfo.js.map